import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["children", "images", "sx"],
  _excluded2 = ["images", "alt"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var pixelDensities = ['1x', '2x', '3x'];
var breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
var formats = ['webp', 'avif'];

/**
 * This hook was taken from MUI: https://mui.com/material-ui/react-use-media-query/#migrating-from-withwidth
 * Be careful using this hook. It only works because the number of
 * breakpoints in theme is static. It will break once you change the number of
 * breakpoints. See https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
 * @function useWidth
 * @return {String}
 */
function useWidth() {
  var theme = useTheme();
  var keys = _toConsumableArray(theme.breakpoints.keys).reverse();
  return keys.reduce(function (output, key) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    var matches = useMediaQuery(theme.breakpoints.up(key));
    return !output && matches ? key : output;
  }, null) || 'xs';
}

/**
 * Resolves to true or false based on whether the AVIF format is supported in the current
 * browser. The value is cached on the `window` object since it should never be able to
 * change.
 * @function isAvifSupported
 * @return {Promise}
 */
function isAvifSupported() {
  return _isAvifSupported.apply(this, arguments);
}

/**
 * Given an array of image paths, determine which breakpoints, formats, and
 * pixel densities are present. The resulting array is sorted in order of 'best'
 * to 'worst' image based on specificity and file size.
 * @function analyzeImages
 * @param {Array} images An array of image paths
 * @return {Array}
 */
function _isAvifSupported() {
  _isAvifSupported = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var image;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          if (!(window.isAvifSupportedState === undefined)) {
            _context.next = 12;
            break;
          }
          image = new Image();
          image.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
          _context.prev = 3;
          _context.next = 6;
          return image.decode();
        case 6:
          window.isAvifSupportedState = true;
          _context.next = 12;
          break;
        case 9:
          _context.prev = 9;
          _context.t0 = _context["catch"](3);
          window.isAvifSupportedState = false;
        case 12:
          return _context.abrupt("return", window.isAvifSupportedState);
        case 13:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[3, 9]]);
  }));
  return _isAvifSupported.apply(this, arguments);
}
function analyzeImages() {
  var images = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var hasTag = function hasTag(imagePath, tag) {
    return (
      // checking for . and - to account for fingerprinting styles for both CRA and vite
      new RegExp("\\.".concat(tag, "(\\-|\\.)")).test(imagePath) || imagePath.endsWith(".".concat(tag))
    );
  };
  return images.map(function (i) {
    var density = pixelDensities.find(function (d) {
      return hasTag(i, d);
    });
    var format = formats.find(function (f) {
      return hasTag(i, f);
    });
    var breakpoint = breakpoints.find(function (b) {
      return hasTag(i, b);
    });
    var densityIndex = pixelDensities.indexOf(density);
    var formatIndex = formats.indexOf(format);
    var breakpointIndex = breakpoints.indexOf(breakpoint);
    return {
      path: i,
      density: density,
      densityIndex: densityIndex,
      format: format,
      formatIndex: formatIndex,
      breakpoint: breakpoint,
      breakpointIndex: breakpointIndex,
      score: densityIndex + formatIndex + breakpointIndex
    };
  }).sort(function (a, b) {
    return b.score - a.score;
  });
}

/**
 * Split up images by file format for creating source elements. The result is reversed
 * since we want browsers to take the best format they can utilize.
 * @function filterByFormat
 * @param {Array} analyzed An array of images that have already been analyzed and tagged
 * @return {Array}
 */
function filterByFormat(analyzed) {
  return formats.map(function (f) {
    return {
      format: f,
      images: analyzed.filter(function (i) {
        return i.format === f;
      })
    };
  }).filter(function (f) {
    return f.images.length;
  }).reverse();
}

/**
 * Split up images by breakpoint for creating source elements. The result is reversed
 * since we want browsers to take the breakpoint they can utilize.
 * @function filterByBreakpoint
 * @param {Array} analyzed An array of images that have already been analyzed and tagged
 * @return {Array}
 */
function filterByBreakpoint(analyzed) {
  return breakpoints.map(function (b) {
    return {
      breakpoint: b,
      images: analyzed.filter(function (i) {
        return i.breakpoint === b;
      })
    };
  }).filter(function (b) {
    return b.images.length;
  }).reverse();
}

/**
 * Returns a string for use in the media attribute on a source element.
 * @function createMediaAttr
 * @param {Object} theme An MUI theme
 * @param {String} muiBreakpoint The MUI breakpoint to use as reference
 * @return {String}
 */
function createMediaAttr(theme, muiBreakpoint) {
  return theme.breakpoints.up(muiBreakpoint).replace('@media ', '');
}

/**
 * Returns a string for assignment to a srcSet property.
 * @function createSrcsetAttr
 * @param {Array} analyzed An array of images that have already been analyzed and tagged
 * @return {String}
 */
function createSrcsetAttr(analyzed) {
  return analyzed.map(function (i) {
    return i.density ? "".concat(i.path, " ").concat(i.density) : i.path;
  })
  // Adding a space after any path that is joined by `,`. Spaces are necessary between each image listed in a `srcset`,
  // if there is no space around the comma the `<Source>` will not fetch the image properly.
  .join(', ');
}

/**
 * Given analyzed images, return the 'worst' image path based on scoring the tags in order
 * of specificity.
 * @function chooseFallbackImage
 * @param {Array} analyzed An array of images that have already been analyzed and tagged
 * @return {String}
 */
function chooseFallbackImage(analyzed) {
  var _analyzed;
  return ((_analyzed = analyzed[analyzed.length - 1]) === null || _analyzed === void 0 ? void 0 : _analyzed.path) || '';
}

/**
 * Given an array of images, return the best one for the current browser and display
 * capabilities. Since AVIF support is async, this hook will return an empty string
 * until AVIF support is determined.
 * @function useBestImage
 * @param {Array} images An array of image paths
 * @return {String}
 */
function useBestImage(images) {
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    avifSupported = _useState2[0],
    avifSupportedSet = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    bestImage = _useState4[0],
    bestImageSet = _useState4[1];
  var muiWidth = useWidth();

  // Only check for AVIF support if we don't already know it, otherwise
  // this will always trigger the useEffect and cascading renders
  if (avifSupported === undefined) isAvifSupported().then(function (isSupported) {
    return avifSupportedSet(isSupported);
  });
  var analyzed = analyzeImages(images);
  useEffect(function () {
    // If we don't yet know AVIF support, don't attempt to return an image
    if (avifSupported === undefined) return;
    var densityIndex = pixelDensities.indexOf("".concat(window.devicePixelRatio, "x"));
    var formatIndex = avifSupported ? 1 : 0;
    var breakpointIndex = breakpoints.indexOf(muiWidth);
    var calculatedImage = analyzed.find(function (i) {
      return i.densityIndex <= densityIndex && i.formatIndex <= formatIndex && i.breakpointIndex <= breakpointIndex;
    });
    bestImageSet(calculatedImage ? calculatedImage === null || calculatedImage === void 0 ? void 0 : calculatedImage.path : chooseFallbackImage(analyzed));
  }, [muiWidth, avifSupported]);
  return bestImage;
}

/**
 * Given an array of image filenames, chooses the most appropriate image to
 * set as a background given the current user's browser.
 */
function BoxBackground(_ref) {
  var _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children,
    images = _ref.images,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    args = _objectWithoutProperties(_ref, _excluded);
  var bestImage = useBestImage(images);
  return /*#__PURE__*/jsx(Box, _objectSpread(_objectSpread({}, args), {}, {
    sx: _objectSpread({
      backgroundImage: "url(".concat(bestImage, ")")
    }, sx),
    children: children
  }));
}
BoxBackground.propTypes = {
  children: PropTypes.object,
  images: PropTypes.array.isRequired,
  sx: PropTypes.object
};
function PictureHelper(_ref2) {
  var images = _ref2.images,
    alt = _ref2.alt,
    args = _objectWithoutProperties(_ref2, _excluded2);
  var theme = useTheme();
  var analyzed = analyzeImages(images);
  var formatMimeType = {
    webp: 'image/webp',
    avif: 'image/avif'
  };
  // Split the image list by type
  var analyzedByFormat = filterByFormat(analyzed);
  var fallbackImage = chooseFallbackImage(analyzed);
  if (!images.length) return null;
  return /*#__PURE__*/jsxs("picture", {
    children: [analyzedByFormat.map(function (f) {
      return filterByBreakpoint(f.images).map(function (b) {
        return /*#__PURE__*/jsx("source", {
          srcSet: createSrcsetAttr(b.images),
          media: createMediaAttr(theme, b.breakpoint),
          type: formatMimeType[f.format],
          "data-testid": "source-".concat(f.format, "-").concat(b.breakpoint)
        }, "".concat(f.format, "-").concat(b.breakpoint));
      });
    }), fallbackImage && /*#__PURE__*/jsx("img", _objectSpread(_objectSpread({}, args), {}, {
      src: fallbackImage,
      alt: alt
    }))]
  });
}
PictureHelper.propTypes = {
  images: PropTypes.array.isRequired,
  alt: PropTypes.string.isRequired
};

/**
 * Given an object of contentful image that contains only 1x image for 3 breakpoints: xs, sm, md.
 * This function will retrieve 1x, 2x and 3x images in 2 formats we support using [contentful Images API](https://www.contentful.com/developers/docs/references/images-api/).
 * The resulting array is sorted in order of 'best' to 'worst' image based on specificity and file size.
 * @function analyzeImages
 * @param {Object} image An object of contentful image
 * @return {Array}
 */
function analyzeContentfulImages() {
  var image = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var analyzedImages = [];
  var getImagesPerScreenSize = function getImagesPerScreenSize(imageObj, screenSize) {
    var url = imageObj.url,
      width = imageObj.width,
      height = imageObj.height;
    if (url) {
      pixelDensities.forEach(function (d, dIndex) {
        formats.forEach(function (f, fIndex) {
          // score is add up of pixelDensityIndex, formatIndex and breakpointIndex
          // because the 3 arrays: pixelDensities, formats and breakpoints are in order of least to most 'desired' or specific
          // So we can use score to sort the analyzedImages array. This could help us to pick the fall back image in 'chooseFallbackImage' function below
          var score = dIndex + fIndex + breakpoints.indexOf(screenSize);

          // Contentful Images API limits height and width to a max of 4,000 pixels
          // https://www.contentful.com/developers/docs/references/images-api/#/reference/resizing-&-cropping
          // Only push images under the max height/width limit
          var calcHeight = height * (dIndex + 1);
          var calcWidth = width * (dIndex + 1);
          var maxDimension = 4000; // max allowed height/width by Contentful API

          if (calcHeight <= maxDimension && calcWidth <= maxDimension) {
            analyzedImages.push({
              path: "".concat(url, "?w=").concat(calcWidth, "&h=").concat(calcHeight, "&fm=").concat(f),
              density: d,
              format: f,
              breakpoint: screenSize,
              score: score
            });
          }
        });
      });
    }
  };
  if (image.imageXs) {
    getImagesPerScreenSize(image.imageXs, 'xs');
  }
  if (image.imageSm) {
    getImagesPerScreenSize(image.imageSm, 'sm');
  }
  if (image.imageMd) {
    getImagesPerScreenSize(image.imageMd, 'md');
  }
  return analyzedImages.sort(function (a, b) {
    return b.score - a.score;
  });
}

/**
 * Given a contentful image object, this component generates a picture element with the correct internal source elements so that the user's browser can choose the proper image to show.
 */
function PictureHelperContentful(_ref3) {
  var image = _ref3.image,
    _ref3$width = _ref3.width,
    width = _ref3$width === void 0 ? '100%' : _ref3$width,
    _ref3$height = _ref3.height,
    height = _ref3$height === void 0 ? 'auto' : _ref3$height,
    _ref3$altText = _ref3.altText,
    altText = _ref3$altText === void 0 ? '' : _ref3$altText,
    _ref3$objectFit = _ref3.objectFit,
    objectFit = _ref3$objectFit === void 0 ? 'cover' : _ref3$objectFit;
  var theme = useTheme();
  var analyzed = analyzeContentfulImages(image);
  var formatMimeType = {
    webp: 'image/webp',
    avif: 'image/avif'
  };
  // Split the image list by type
  var analyzedByFormat = filterByFormat(analyzed);
  var fallbackImage = chooseFallbackImage(analyzed);
  if (!analyzed.length) return null;
  return /*#__PURE__*/jsxs("picture", {
    children: [analyzedByFormat.map(function (f) {
      return filterByBreakpoint(f.images).map(function (b) {
        return /*#__PURE__*/jsx("source", {
          srcSet: createSrcsetAttr(b.images),
          media: createMediaAttr(theme, b.breakpoint),
          type: formatMimeType[f.format]
        }, "".concat(f.format, "-").concat(b.breakpoint));
      });
    }), fallbackImage && /*#__PURE__*/jsx("img", {
      width: width,
      height: height,
      style: {
        maxWidth: '1920px',
        objectFit: objectFit
      },
      src: fallbackImage,
      alt: altText
    })]
  });
}
PictureHelperContentful.propTypes = {
  /**
   * Consumer need to pass in image object from contenful model.
   */
  image: PropTypes.shape({
    imageXs: PropTypes.shape({
      url: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number
    }),
    imageSm: PropTypes.shape({
      url: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number
    }),
    imageMd: PropTypes.shape({
      url: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number
    })
  }).isRequired,
  /**
   * This attribute holds a textual replacement for the image, which is mandatory and incredibly useful for accessibility — screen readers read the attribute value out to their users so they know what the image means. Alt text is also displayed on the page if the image can't be loaded for some reason: for example, network errors, content blocking, or linkrot
   */
  altText: PropTypes.string,
  /**
   * Consumer can pass in this width for the image container.
   */
  width: PropTypes.string,
  /**
   * Consumer can pass in this height for the image container.
   */
  height: PropTypes.string,
  /**
   * Use this prop if you have a need to change object-fit to something else like 'fill' or 'contain'.
   * Check here for valid object-fit value: https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
   */
  objectFit: PropTypes.string
};

export { BoxBackground, PictureHelper, PictureHelperContentful, isAvifSupported, useBestImage, useWidth };
//# sourceMappingURL=index.esm.js.map
